import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { withOktaAuth } from '@okta/okta-react';
import type { OktaAuth, AuthState } from '@okta/okta-auth-js';
import classNames from 'classnames';


import { Stack } from 'ui/stack';
import { Box } from 'ui/box';

import FlashNotification from 'components/partials/flash-notification/flash-notification';

import { getApiInfo } from 'api/api.config';
import { isNonProdEnv } from 'helpers/app-env';

import Icons from 'assets/icons';

import svgBackground from 'assets/images/login-bg.svg';

interface OktaInjectedProps {
  oktaAuth: OktaAuth;
  authState: AuthState | null;
}

interface LoginLayoutProps extends OktaInjectedProps {
  children?: ReactNode;
  // `auth` is a leftover prop from a previous Okta integration; it is not
  // currently injected by withOktaAuth and is always undefined at runtime.
  auth?: { isAuthenticated: () => Promise<boolean> };
}

interface ApiInfoResponse {
  data?: {
    git?: { commit?: { id?: { abbrev?: string } } };
    build?: { time?: string };
  };
}

const LoginLayout = ({
  auth,
  children,
}: LoginLayoutProps): React.JSX.Element => {
  const [authenticated, setAuthenticated] = useState<
    boolean | null | undefined
  >(null);
  const [commitInfo, setCommitInfo] = useState('');

  const [showLogo, setShowLogo] = useState(false);
  const [showText, setShowText] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [svgBackgroundUrl, setSvgBackgroundUrl] = useState('');

  const isNonProd = isNonProdEnv();

  useEffect(() => {
    if (!isNonProd) return;

    const fetchApiInfo = async (): Promise<void> => {
      try {
        const resp = (await getApiInfo()) as ApiInfoResponse;
        const data = resp.data;

        if (data?.git?.commit && data.build) {
          const infoText = `${data.git.commit.id?.abbrev} built on ${data.build.time}`;

          setCommitInfo(infoText);
        }
      } catch (err) {
        console.error('Error fetching API info:', err);
      }
    };

    fetchApiInfo();
  }, [isNonProd]);

  useEffect(() => {
    const bgUrlWithTimestamp = `${svgBackground}?${Date.now()}`;

    setSvgBackgroundUrl(bgUrlWithTimestamp);
  }, []);

  useEffect(() => {
    const checkAuthentication = async (): Promise<void> => {
      const isAuthenticated = await auth?.isAuthenticated();

      if (isAuthenticated !== authenticated) {
        setAuthenticated(isAuthenticated);
      }
    };

    checkAuthentication();

    const timer1 = setTimeout(() => setShowLogo(true), 200);
    const timer2 = setTimeout(() => setShowText(true), 500);
    const timer3 = setTimeout(() => setShowForm(true), 700);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [auth, authenticated]);

  return authenticated ? (
    <Navigate replace to="/" />
  ) : (
    <div
      className="min-h-screen flex justify-start mobile:p-5 tablet:pt-24 pt-36 bg-cover bg-center relative login-layout-wrapper"
      style={{ backgroundImage: `url(${svgBackgroundUrl})` }}
    >
      <Stack sx={{ width: '100%', alignItems: 'center' }}>
        <FlashNotification customClass="mb-10" />

        <Stack sx={{ justifyContent: 'center', alignItems: 'center', mb: 1 }}>
          <div
            className={classNames('transition-all duration-700', {
              'opacity-100 translate-y-0': showLogo,
              'opacity-0 -translate-y-10': !showLogo,
            })}
          >
            <Icons.LoginLogo
              role="img"
              aria-label="MARKETview"
              className="mobile:w-40 mobile:h-10 tablet:h-14 tablet:w-52"
            />
          </div>
        </Stack>

        <Box
          sx={{
            transitionProperty: 'opacity',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDuration: '700ms',
            opacity: showText ? 1 : 0,
          }}
        >
          <p className="font-h-1 text-navy-800 font-family font-normal text-center block mobile:text-xl tablet:text-3xl">
            Achieve your <span className="font-semibold">goals</span> with{' '}
            <span className="font-semibold"> actionable steps </span> informed
            by your <span className="font-semibold">Critical Elements.</span>
          </p>
        </Box>

        <Stack
          direction="row"
          sx={{
            width: '100%',
            justifyContent: 'center',
            '@media (min-width: 375px)': { mt: 2.5 },
            '@media (min-width: 780px)': { mt: 5 },
          }}
        >
          <div
            className={classNames(
              'bg-white flex flex-col items-center justify-center mobile:w-[335px] tablet:w-[500px] mobile:px-6 tablet:px-10 py-8 rounded-md transition-all duration-700',
              {
                'opacity-100 translate-y-0': showForm,
                'opacity-0 translate-y-10': !showForm,
              }
            )}
            style={{ boxShadow: '0px 2px 8px -2px rgba(0, 45, 93, 0.20)' }}
          >
            <Box sx={{ width: '100%' }}>{children}</Box>
          </div>
        </Stack>
        {isNonProd && (
          <span className="absolute bottom-8 font-sm">{commitInfo}</span>
        )}
      </Stack>
    </div>
  );
};

export default withOktaAuth(LoginLayout);
